if ( typeof ProductImageZoom !== 'function' ) {

  class ProductImageZoom extends HTMLElement {

    constructor() {
      super();

      // --- Create / reuse the zoom modal ---
      if ( !document.getElementById('zoom') ) {

        this.zoom = document.createElement('div');
        this.zoom.id = 'zoom';

        const arrowLeft  = (KROWN?.settings?.symbols?.arrow_left)  || '‹';
        const arrowRight = (KROWN?.settings?.symbols?.arrow_right) || '›';
        const zoomOut    = (KROWN?.settings?.symbols?.zoom_out)    || '×';
        const zoomLoader = (KROWN?.settings?.symbols?.zoom_loader) || '';

        this.zoom.innerHTML = `
          <img />
          <button class="zoom__nav zoom__nav--left" type="button" aria-label="Previous image">${arrowLeft}</button>
          <button class="zoom__nav zoom__nav--right" type="button" aria-label="Next image">${arrowRight}</button>
          <span class="zoom__exit" aria-label="Close zoom" role="button" tabindex="0">${zoomOut}</span>
          <span class="zoom__loader">${zoomLoader}</span>
          <div class="zoom__overlay"></div>
        `;

        document.body.append(this.zoom);

        this.zoom.querySelector('.zoom__exit')
          .addEventListener('click', this._productZoomUnmount.bind(this));

        this.zoom.querySelector('.zoom__overlay')
          .addEventListener('click', this._productZoomUnmount.bind(this));

        this.zoom.querySelector('.zoom__exit')
          .addEventListener('keydown', (e) => {
            if ( e.key === 'Enter' || e.key === ' ' ) {
              e.preventDefault();
              this._productZoomUnmount();
            }
          });

      } else {
        this.zoom = document.getElementById('zoom');
      }

      // --- Bind handlers ---
      this.onMouseMoveHandlerBinded = this.onMouseMoveHandler.bind(this);
      this.onResizeHandlerBinded = this.onResizeHandler.bind(this);

      // --- Wire nav buttons once ---
      const prevBtn = this.zoom.querySelector('.zoom__nav--left');
      const nextBtn = this.zoom.querySelector('.zoom__nav--right');

      if ( prevBtn && nextBtn && !this.zoom.dataset.navBound ) {

        this.zoom.dataset.navBound = '1';

        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._zoomPrev();
        });

        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._zoomNext();
        });

        document.addEventListener('keydown', (e) => {
          if ( !this.zoom.classList.contains('opened') ) return;

          if ( e.key === 'ArrowLeft' ) {
            e.preventDefault();
            this._zoomPrev();
          } else if ( e.key === 'ArrowRight' ) {
            e.preventDefault();
            this._zoomNext();
          } else if ( e.key === 'Escape' ) {
            e.preventDefault();
            this._productZoomUnmount();
          }
        });

      }

      // --- Open zoom on click ---
      this.addEventListener('click', (e) => {
        e.preventDefault();

        document.body.classList.add('zoom-open');

        this.zoom.classList.add('opened');
        this.zoom.classList.remove('loaded');
        this.zoom.scrollTop = 0;
        this.zoom.scrollLeft = 0;

        const items = this._getZoomItems();
        this.currentZoomIndex = Math.max(0, items.indexOf(this));

        this.image = this.zoom.querySelector('img');
        this._loadAndMount(this.getAttribute('data-image'));
      });

    }

    _isMobileZoom() {
      return window.innerWidth <= 768 || 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    /**
     * Returns the list of <product-image-zoom> elements to rotate through.
     */
    _getZoomItems() {
      const scope =
        this.closest('[data-product-media]') ||
        this.closest('.product__media') ||
        this.closest('.product-media') ||
        this.closest('.product-gallery') ||
        this.closest('.product') ||
        document;

      return Array.from(scope.querySelectorAll('product-image-zoom'))
        .filter((el) => !!el.getAttribute('data-image'));
    }

    _loadAndMount(src) {
  if (!src) return;

  this.zoom.classList.remove('loaded');

  this.image = this.zoom.querySelector('img');
  if (!this.image) return;

  const preloader = new Image();

  preloader.onload = () => {
    this.image.src = src;
    this._productZoomMount();
  };

  preloader.onerror = () => {
    this.zoom.classList.add('loaded');
  };

  preloader.src = src;
}

    _setZoomImageByIndex(nextIndex, direction = 'right') {
      const items = this._getZoomItems();
      if ( items.length <= 1 ) return;

      const idx = (nextIndex + items.length) % items.length;
      this.currentZoomIndex = idx;

      const el = items[idx];
      const src = el.getAttribute('data-image');
      if ( !src ) return;

      const img = this.zoom.querySelector('img');
      if (!img) return;

      if (!this.zoom.classList.contains('opened')) {
        this._loadAndMount(src);
        return;
      }

      this.zoom.classList.remove('loaded');

      window.removeEventListener('mousemove', this.onMouseMoveHandlerBinded);

      const outClass = direction === 'right' ? 'zoom-slide-out-left' : 'zoom-slide-out-right';
      const inClass  = direction === 'right' ? 'zoom-slide-in-right'  : 'zoom-slide-in-left';

      img.classList.remove(
        'zoom-slide-out-left','zoom-slide-out-right',
        'zoom-slide-in-left','zoom-slide-in-right',
        'zoom-slide-active'
      );

      img.classList.add(outClass);

      setTimeout(() => {
        img.src = src;

        const onLoaded = () => {
          img.classList.remove(outClass);
          img.classList.add(inClass);

          requestAnimationFrame(() => {
            img.classList.add('zoom-slide-active');
            img.classList.remove(inClass);
          });

          this._productZoomMount();
        };

        if (img.complete && img.naturalWidth > 0) {
          onLoaded();
        } else {
          img.addEventListener('load', onLoaded, { once: true });
        }

      }, 260);
    }

    _zoomNext() {
      this._setZoomImageByIndex((this.currentZoomIndex ?? 0) + 1, 'right');
    }

    _zoomPrev() {
      this._setZoomImageByIndex((this.currentZoomIndex ?? 0) - 1, 'left');
    }

    /**
     * Desktop-only mouse panning.
     */
    onMouseMoveHandler(e) {
      if (this._isMobileZoom()) return;

      const img = this.image;
      if (!img) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const iw = img.offsetWidth;
      const ih = img.offsetHeight;

      const minX = Math.min(0, vw - iw);
      const minY = Math.min(0, vh - ih);

      const nx = e.clientX / vw;
      const ny = e.clientY / vh;

      const x = minX * nx;
      const y = minY * ny;

      img.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }

    onResizeHandler() {
      if (!this.image) return;

      const isMobile = this._isMobileZoom();

      this.image.dataset.ratio = this.image.naturalWidth / this.image.naturalHeight;

      if (isMobile) {
        // Mobile: fitted lightbox behavior
        this.image.style.position = 'absolute';
        // this.image.style.width = 'auto';
        // this.image.style.height = 'auto';
        this.image.style.maxWidth = '100wv';
        this.image.style.maxHeight = '100dvh';
        // this.image.style.left = '50%';
        // this.image.style.top = '50%';
        // this.image.style.transform = 'translate(-50%, -50%)';
        return;
      }

      // Desktop: oversize image for pan/zoom effect
      const rf = 1;

      this.image.style.maxWidth = 'none';
      this.image.style.maxHeight = 'none';
      this.image.style.position = 'absolute';
      this.image.style.left = '0px';
      this.image.style.top = '0px';

      if ( this.image.classList.contains('portrait') ) {
        this.image.style.width = ( window.innerWidth * rf ) + 'px';
        this.image.style.height = ( window.innerWidth * rf / this.image.dataset.ratio ) + 'px';
      } else {
        this.image.style.height = ( window.innerHeight * rf ) + 'px';
        this.image.style.width = ( window.innerHeight * rf * this.image.dataset.ratio ) + 'px';

        if ( this.image.offsetWidth < window.innerWidth ) {
          this.image.style.width = ( window.innerWidth * rf ) + 'px';
          this.image.style.height = ( window.innerWidth * rf / this.image.dataset.ratio ) + 'px';
        }
      }

      this.image.style.transform = 'translate3d(0,0,0)';
    }

    _productZoomMount() {
      if (!this.image) return;

      const isMobile = this._isMobileZoom();

      this.image.style.willChange = 'transform';
      this.image.dataset.ratio = this.image.naturalWidth / this.image.naturalHeight;

      // Reset listeners before rebinding
      window.removeEventListener('mousemove', this.onMouseMoveHandlerBinded);
      window.removeEventListener('resize', this.onResizeHandlerBinded);

      if (!isMobile) {
        this.image.style.left = '0px';
        this.image.style.top = '0px';
        this.image.style.transform = 'translate3d(0,0,0)';
        window.addEventListener('mousemove', this.onMouseMoveHandlerBinded, { passive: true });
      }

      window.addEventListener('resize', this.onResizeHandlerBinded, { passive: true });
      this.onResizeHandler();

      setTimeout(() => {
        if (
          !isMobile &&
          (document.body.classList.contains('touch') || document.body.classList.contains('touchevents'))
        ) {
          this.zoom.scrollTop  = ( window.innerHeight - this.image.offsetHeight ) / -2;
          this.zoom.scrollLeft = ( window.innerWidth  - this.image.offsetWidth ) / -2;
        }

        this.zoom.classList.add('loaded');
      }, 50);
    }

    _productZoomUnmount() {
      this.zoom.classList.remove('opened');
      document.body.classList.remove('zoom-open');

      setTimeout(() => {
        window.removeEventListener('resize', this.onResizeHandlerBinded);
        window.removeEventListener('mousemove', this.onMouseMoveHandlerBinded);

        const image = this.zoom.querySelector('img');
        if (image) {
          image.src = '';
          image.className = '';
          image.removeAttribute('style');
          image.removeAttribute('data-ratio');
        }

        this.zoom.classList.remove('loaded');
        this.zoom.scrollTop = 0;
        this.zoom.scrollLeft = 0;
      }, 300);
    }

  }

  if ( typeof customElements.get('product-image-zoom') === 'undefined' ) {
    customElements.define('product-image-zoom', ProductImageZoom);
  }

}